import { Link, useParams } from 'react-router-dom';
import { Activity, ArrowLeft, Flame, ListTree, Shield, Target, Users, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { GuidedEmpty } from '../components/GuidedEmpty';
import { ModuleHero } from '../components/ModuleHero';
import { PublicEventSection } from '../components/PageTemplates';
import { useStore } from '../hooks/useStore';
import { useActiveTournament } from '../state/TournamentContext';
import { buildDeterministicEventAnalytics, type TeamAnalyticsSummary } from '../services/deterministicAnalyticsService';

export function PublicTeamProfilePage() {
  const { teamId = '' } = useParams<{ teamId: string }>();
  const db = useStore((row) => row);
  const { tournament } = useActiveTournament();
  const analytics = useMemo(() => buildDeterministicEventAnalytics(db, tournament.id), [db, tournament.id]);
  const team = analytics?.teams.find((row) => row.teamId === teamId);
  const division = db.divisions.find((row) => row.id === team?.divisionId);

  if (!team || !analytics) {
    return (
      <GuidedEmpty
        icon={Shield}
        title="Team not found"
        body="This team is not attached to the current public event."
        action={{ to: `/events/${tournament.slug}/stats`, label: 'Back to stats' }}
      />
    );
  }

  return (
    <PublicEventSection>
    <div className="public-profile">
      <Link className="btn btn--ghost btn--sm" to={`/events/${tournament.slug}/stats`}>
        <ArrowLeft size={14} /> Back to stats
      </Link>

      <ModuleHero
        eyebrow={division?.name ?? (tournament.format === 'season' ? 'Season team' : 'Event team')}
        title={team.teamName}
        description={`${team.wins}-${team.losses}-${team.ties}, ${team.points} points, ${signedNumber(team.goalDifferential)} goal differential.`}
        icon={Shield}
        accent="arc"
        status={{ label: team.currentStreak, tone: team.currentStreak.startsWith('W') ? 'ok' : 'info' }}
      />

      <section className="public-stats__kpis">
        <StatTile label="Record" value={`${team.wins}-${team.losses}-${team.ties}`} detail={`${team.points} points`} />
        <StatTile label="Goals" value={`${team.goalsFor}-${team.goalsAgainst}`} detail={`${formatNumber(team.goalsForPerGame)} GF/G`} />
        <StatTile label="Differential" value={signedNumber(team.goalDifferential)} detail={`${formatNumber(team.goalsAgainstPerGame)} GA/G`} />
        <StatTile label="PIM" value={team.penaltyMinutes} detail="Team penalty minutes" />
      </section>

      <div className="grid grid--2">
        <TeamCard title="Team form" icon={Flame}>
          <div className="public-profile__form-row">
            {team.recentForm.length === 0 ? (
              <span className="muted">Recent form appears after final scores.</span>
            ) : (
              team.recentForm.map((result, index) => (
                <span key={`${result}-${index}`} className={`public-event-intel__form-pill public-event-intel__form-pill--${result.toLowerCase()}`}>
                  {result}
                </span>
              ))
            )}
          </div>
          <MetricList
            rows={[
              ['Longest win streak', team.longestWinStreak],
              ['Longest point streak', team.longestPointStreak],
              ['Comeback wins', team.comebackWins],
              ['One-goal games', team.oneGoalGames],
              ['Shutouts', team.shutouts],
              ['OT record', `${team.overtimeWins}-${team.overtimeLosses}`],
              ['SO record', `${team.shootoutWins}-${team.shootoutLosses}`],
            ]}
          />
        </TeamCard>

        <TeamCard title="Special teams and pressure" icon={Target}>
          <MetricList
            rows={[
              ['Power-play goals', team.powerPlayGoals],
              ['Short-handed goals', team.shortHandedGoals],
              ['Goals per game', formatNumber(team.goalsForPerGame)],
              ['Goals against per game', formatNumber(team.goalsAgainstPerGame)],
              ['Penalty minutes', team.penaltyMinutes],
            ]}
          />
        </TeamCard>
      </div>

      <div className="grid grid--2">
        <TopPlayersCard team={team} slug={tournament.slug} />
        <HeadToHeadCard team={team} slug={tournament.slug} />
      </div>

      <TeamCard title="Scoring by period" icon={Activity}>
        {team.scoringByPeriod.length === 0 ? (
          <p className="muted">Period splits appear once scorer-table events are recorded.</p>
        ) : (
          <div className="table-scroll">
            <table className="table public-stats__table">
              <thead>
                <tr><th>Period</th><th>GF</th><th>GA</th><th>Shots</th><th>PIM</th><th>PPG</th><th>SHG</th></tr>
              </thead>
              <tbody>
                {team.scoringByPeriod.map((row) => (
                  <tr key={row.period}>
                    <td>P{row.period}</td>
                    <td>{row.goalsFor}</td>
                    <td>{row.goalsAgainst}</td>
                    <td>{row.shotsFor}-{row.shotsAgainst}</td>
                    <td>{row.penaltyMinutes}</td>
                    <td>{row.powerPlayGoals}</td>
                    <td>{row.shortHandedGoals}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </TeamCard>
    </div>
    </PublicEventSection>
  );
}

function TopPlayersCard({ team, slug }: { team: TeamAnalyticsSummary; slug: string }) {
  return (
    <TeamCard title="Top players" icon={Users}>
      {team.topPlayers.length === 0 ? (
        <p className="muted">Player contributions appear after scoring events.</p>
      ) : (
        <div className="public-profile__list">
          {team.topPlayers.slice(0, 8).map((player) => (
            <Link key={player.playerId} to={`/events/${slug}/players/${player.playerId}`} className="public-profile__row-link">
              <span>{player.playerName}</span>
              <strong>{player.points} pts</strong>
            </Link>
          ))}
        </div>
      )}
    </TeamCard>
  );
}

function HeadToHeadCard({ team, slug }: { team: TeamAnalyticsSummary; slug: string }) {
  return (
    <TeamCard title="Head-to-head" icon={ListTree}>
      {team.headToHead.length === 0 ? (
        <p className="muted">Opponent splits appear after final scores.</p>
      ) : (
        <div className="public-profile__list">
          {team.headToHead.map((row) => (
            <Link key={row.opponentTeamId} to={`/events/${slug}/teams/${row.opponentTeamId}`} className="public-profile__row-link">
              <span>{row.opponentName}</span>
              <strong>{row.wins}-{row.losses}-{row.ties}</strong>
            </Link>
          ))}
        </div>
      )}
    </TeamCard>
  );
}

function TeamCard({ title, icon: Icon, children }: { title: string; icon: LucideIcon; children: ReactNode }) {
  return (
    <section className="card public-profile__card">
      <div className="card__head">
        <h3 className="card__title">
          <Icon size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
          {title}
        </h3>
      </div>
      {children}
    </section>
  );
}

function MetricList({ rows }: { rows: Array<[string, number | string]> }) {
  return (
    <div className="public-profile__metric-list">
      {rows.map(([label, value]) => (
        <div key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  );
}

function StatTile({ label, value, detail }: { label: string; value: string | number; detail: string }) {
  return (
    <div className="public-stats__kpi">
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{detail}</p>
    </div>
  );
}

function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return '0';
  return value % 1 === 0 ? String(value) : value.toFixed(1);
}

function signedNumber(value: number): string {
  if (value > 0) return `+${value}`;
  return String(value);
}
